/**
 * Application Status & Incident Management Page
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTime } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  AlertTriangle,
  AlertOctagon,
  Wrench,
  MinusCircle,
} from 'lucide-react';

interface AppStatus {
  id: string;
  applicationId: string;
  applicationName: string;
  status: string;
  message: string | null;
  updatedAt: string;
}

interface Incident {
  id: string;
  applicationId: string;
  applicationName: string;
  title: string;
  description: string | null;
  severity: string;
  status: string;
  startedAt: string;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Application {
  id: string;
  name: string;
}

const statusIndicatorColors: Record<string, string> = {
  operational: 'bg-green-500',
  degraded: 'bg-yellow-500',
  partial_outage: 'bg-orange-500',
  major_outage: 'bg-red-500',
  maintenance: 'bg-blue-500',
};

const statusLabels: Record<string, string> = {
  operational: 'Operational',
  degraded: 'Degraded Performance',
  partial_outage: 'Partial Outage',
  major_outage: 'Major Outage',
  maintenance: 'Under Maintenance',
};

const severityColors: Record<string, string> = {
  minor: 'bg-blue-100 text-blue-800',
  major: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const incidentStatusColors: Record<string, string> = {
  investigating: 'bg-red-100 text-red-800',
  identified: 'bg-orange-100 text-orange-800',
  monitoring: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
};

const incidentStatusLabels: Record<string, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

export function AppStatusManagementPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('status');
  const [selectedAppFilter, setSelectedAppFilter] = useState('');

  // Status update state
  const [statusUpdates, setStatusUpdates] = useState<
    Record<string, { status: string; message: string }>
  >({});

  // Incident dialog state
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingIncident, setDeletingIncident] = useState<Incident | null>(null);

  // Incident form state
  const [formApplicationId, setFormApplicationId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSeverity, setFormSeverity] = useState('minor');
  const [formIncidentStatus, setFormIncidentStatus] = useState('investigating');

  // Fetch applications
  const { data: appsData } = useQuery({
    queryKey: ['finance-applications'],
    queryFn: async () => {
      const result = await api.getFinanceApplications();
      return result;
    },
  });

  const applications = (appsData?.data || []) as Application[];

  // Fetch app statuses
  const { data: statusesData, isLoading: statusesLoading } = useQuery({
    queryKey: ['app-statuses'],
    queryFn: () => api.getAppStatuses(),
  });

  const appStatuses = (statusesData?.data || []) as AppStatus[];

  // Fetch incidents for selected app
  const { data: incidentsData, isLoading: incidentsLoading } = useQuery({
    queryKey: ['incidents', selectedAppFilter],
    queryFn: () => api.getIncidents(selectedAppFilter),
    enabled: !!selectedAppFilter,
  });

  const incidents = (incidentsData?.data || []) as Incident[];

  // Update app status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: Record<string, unknown>;
    }) => api.updateAppStatus(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-statuses'] });
      toast({ title: 'Status updated', description: 'Application status has been updated.' });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update status.',
        variant: 'destructive',
      });
    },
  });

  // Incident mutations
  const createIncidentMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createIncident(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['app-statuses'] });
      closeIncidentDialog();
      toast({ title: 'Incident created', description: 'New incident has been created.' });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create incident.',
        variant: 'destructive',
      });
    },
  });

  const updateIncidentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateIncident(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['app-statuses'] });
      closeIncidentDialog();
      toast({ title: 'Incident updated', description: 'Incident has been updated.' });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update incident.',
        variant: 'destructive',
      });
    },
  });

  const deleteIncidentMutation = useMutation({
    mutationFn: (id: string) => api.deleteIncident(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      setDeleteDialogOpen(false);
      setDeletingIncident(null);
      toast({ title: 'Incident deleted', description: 'Incident has been deleted.' });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete incident.',
        variant: 'destructive',
      });
    },
  });

  function getStatusIcon(status: string) {
    switch (status) {
      case 'operational':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'degraded':
        return <MinusCircle className="h-5 w-5 text-yellow-500" />;
      case 'partial_outage':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'major_outage':
        return <AlertOctagon className="h-5 w-5 text-red-500" />;
      case 'maintenance':
        return <Wrench className="h-5 w-5 text-blue-500" />;
      default:
        return <CheckCircle className="h-5 w-5 text-gray-400" />;
    }
  }

  function handleStatusFieldChange(
    applicationId: string,
    field: 'status' | 'message',
    value: string
  ) {
    setStatusUpdates((prev) => ({
      ...prev,
      [applicationId]: {
        ...prev[applicationId],
        [field]: value,
      },
    }));
  }

  function handleUpdateStatus(applicationId: string, currentStatus: string) {
    const update = statusUpdates[applicationId];
    const status = update?.status || currentStatus;
    const message = update?.message || '';
    updateStatusMutation.mutate({
      applicationId,
      data: { status, message: message || null },
    });
  }

  function openCreateIncidentDialog() {
    setEditingIncident(null);
    setFormApplicationId(selectedAppFilter || '');
    setFormTitle('');
    setFormDescription('');
    setFormSeverity('minor');
    setFormIncidentStatus('investigating');
    setIncidentDialogOpen(true);
  }

  function openEditIncidentDialog(incident: Incident) {
    setEditingIncident(incident);
    setFormApplicationId(incident.applicationId);
    setFormTitle(incident.title);
    setFormDescription(incident.description || '');
    setFormSeverity(incident.severity);
    setFormIncidentStatus(incident.status);
    setIncidentDialogOpen(true);
  }

  function closeIncidentDialog() {
    setIncidentDialogOpen(false);
    setEditingIncident(null);
  }

  function handleIncidentSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      applicationId: formApplicationId,
      title: formTitle,
      description: formDescription || null,
      severity: formSeverity,
      status: formIncidentStatus,
    };

    if (editingIncident) {
      updateIncidentMutation.mutate({ id: editingIncident.id, data: payload });
    } else {
      createIncidentMutation.mutate(payload);
    }
  }

  const isSaving = createIncidentMutation.isPending || updateIncidentMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Application Status</h1>
        <p className="text-muted-foreground">Monitor and manage application health and incidents</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
        </TabsList>

        {/* Status Tab */}
        <TabsContent value="status" className="space-y-4">
          {statusesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : appStatuses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No applications found</h3>
                <p className="text-sm text-muted-foreground">
                  No application statuses are being tracked yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {appStatuses.map((app) => {
                const update = statusUpdates[app.applicationId];
                const currentStatus = update?.status || app.status;

                return (
                  <Card key={app.applicationId}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{app.applicationName}</CardTitle>
                        <div className="flex items-center gap-2">
                          <div
                            className={`h-3 w-3 rounded-full ${statusIndicatorColors[app.status] || 'bg-gray-400'}`}
                          />
                          {getStatusIcon(app.status)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <Badge
                          className={`${
                            app.status === 'operational'
                              ? 'bg-green-100 text-green-800'
                              : app.status === 'degraded'
                                ? 'bg-yellow-100 text-yellow-800'
                                : app.status === 'partial_outage'
                                  ? 'bg-orange-100 text-orange-800'
                                  : app.status === 'major_outage'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {statusLabels[app.status] || app.status}
                        </Badge>
                      </div>

                      {app.message && (
                        <p className="text-sm text-muted-foreground">{app.message}</p>
                      )}

                      <p className="text-xs text-muted-foreground">
                        Last updated: {formatDateTime(app.updatedAt)}
                      </p>

                      <div className="border-t pt-3 space-y-2">
                        <Select
                          value={currentStatus}
                          onValueChange={(value) =>
                            handleStatusFieldChange(app.applicationId, 'status', value)
                          }
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="operational">Operational</SelectItem>
                            <SelectItem value="degraded">Degraded Performance</SelectItem>
                            <SelectItem value="partial_outage">Partial Outage</SelectItem>
                            <SelectItem value="major_outage">Major Outage</SelectItem>
                            <SelectItem value="maintenance">Under Maintenance</SelectItem>
                          </SelectContent>
                        </Select>

                        <Input
                          placeholder="Status message (optional)"
                          className="h-8 text-sm"
                          value={update?.message ?? ''}
                          onChange={(e) =>
                            handleStatusFieldChange(app.applicationId, 'message', e.target.value)
                          }
                        />

                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => handleUpdateStatus(app.applicationId, app.status)}
                          disabled={updateStatusMutation.isPending}
                        >
                          {updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Active Incidents</h2>
            <Button onClick={openCreateIncidentDialog}>
              <Plus className="h-4 w-4 mr-2" />
              New Incident
            </Button>
          </div>

          {/* Application filter */}
          <div className="flex items-center gap-4">
            <div className="w-64">
              <Label className="text-sm text-muted-foreground">Filter by Application</Label>
              <Select value={selectedAppFilter} onValueChange={setSelectedAppFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an application" />
                </SelectTrigger>
                <SelectContent>
                  {applications.map((app) => (
                    <SelectItem key={app.id} value={app.id}>
                      {app.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!selectedAppFilter ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertTriangle className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="font-medium">Select an application</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose an application from the dropdown to view its incidents.
                </p>
              </CardContent>
            </Card>
          ) : incidentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : incidents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-10 w-10 mx-auto text-green-500 mb-3" />
                <p className="font-medium">No incidents</p>
                <p className="text-sm text-muted-foreground mt-1">
                  No incidents have been reported for this application.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Application</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Resolved</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.map((incident) => (
                    <TableRow key={incident.id}>
                      <TableCell className="font-medium">
                        {incident.applicationName || 'Unknown'}
                      </TableCell>
                      <TableCell>{incident.title}</TableCell>
                      <TableCell>
                        <Badge className={severityColors[incident.severity] || ''}>
                          {incident.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={incidentStatusColors[incident.status] || ''}>
                          {incidentStatusLabels[incident.status] || incident.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(incident.startedAt || incident.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {incident.resolvedAt ? formatDateTime(incident.resolvedAt) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openEditIncidentDialog(incident)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => {
                              setDeletingIncident(incident);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Incident Dialog */}
      <Dialog open={incidentDialogOpen} onOpenChange={setIncidentDialogOpen}>
        <DialogContent key={editingIncident?.id || 'new'} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingIncident ? 'Edit Incident' : 'New Incident'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleIncidentSubmit} className="space-y-4">
            <div>
              <Label>Application *</Label>
              <Select value={formApplicationId} onValueChange={setFormApplicationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select application" />
                </SelectTrigger>
                <SelectContent>
                  {applications.map((app) => (
                    <SelectItem key={app.id} value={app.id}>
                      {app.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Database connectivity issues"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Describe the incident..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Severity</Label>
                <Select value={formSeverity} onValueChange={setFormSeverity}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select value={formIncidentStatus} onValueChange={setFormIncidentStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="identified">Identified</SelectItem>
                    <SelectItem value="monitoring">Monitoring</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeIncidentDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving || !formTitle || !formApplicationId}
              >
                {isSaving
                  ? 'Saving...'
                  : editingIncident
                    ? 'Update Incident'
                    : 'Create Incident'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Incident</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <strong>{deletingIncident?.title}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deletingIncident && deleteIncidentMutation.mutate(deletingIncident.id)
              }
              disabled={deleteIncidentMutation.isPending}
            >
              {deleteIncidentMutation.isPending ? 'Deleting...' : 'Delete Incident'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
